import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-14 h-14 text-red-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Lien invalide</h1>
          <p className="text-gray-600 mb-6">Ce lien est incomplet. Demandez-en un nouveau.</p>
          <Link to="/mot-de-passe-oublie" className="bg-[#E42313] text-white px-6 py-3 rounded font-semibold">
            Demander un nouveau lien
          </Link>
        </div>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Mot de passe : 6 caractères minimum');
      return;
    }
    if (password !== confirm) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, { token, password });
      if (data.token) {
        localStorage.setItem('access_token', data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      }
      toast.success('Mot de passe réinitialisé');
      navigate('/mon-compte');
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur s'est produite");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8 border border-gray-100">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Nouveau mot de passe</h1>
        <p className="text-gray-600 text-sm mb-6">Choisissez un nouveau mot de passe (6 caractères minimum).</p>

        {error && (
          <div className="mb-5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nouveau mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                data-testid="reset-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border rounded focus:outline-none focus:border-[#E42313]"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Confirmer le mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                data-testid="reset-confirm"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border rounded focus:outline-none focus:border-[#E42313]"
              />
            </div>
          </div>
          <button
            data-testid="reset-submit"
            type="submit"
            disabled={submitting}
            className="w-full bg-[#E42313] text-white py-3 rounded font-semibold hover:bg-[#C11A0F] transition-colors disabled:opacity-50"
          >
            {submitting ? 'Validation…' : 'Réinitialiser'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
